import { useEffect, useState } from 'react';
import { getPortalBranding, type PortalBranding } from '@/api/portal';
import { usePortalAuthStore } from '@/stores/portalAuthStore';

/**
 * Per-tenant white-labeling for the client portal (story 128.2).
 *
 * Mirrors the staff `useBranding` hook but is driven by the portal session and
 * fetches through `portalClient` (the CLIENT_VIEWER token). It injects the
 * tenant's colors + theme tokens + font + favicon so the portal reads as the
 * operator's product. Uses a portal-scoped <style> id so it never collides with
 * the staff theme sheet, and tears everything down on logout so a subsequent
 * login as a different tenant can't inherit the previous theme.
 */
const STYLE_ID = 'kairos-portal-theme';

function applyThemeStyle(tokens: PortalBranding['themeTokens']) {
  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!tokens) {
    style?.remove();
    return;
  }
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }
  const blocks: string[] = [];
  if (tokens.light) {
    blocks.push(`:root { ${Object.entries(tokens.light).map(([k, v]) => `${k}: ${v};`).join(' ')} }`);
  }
  if (tokens.dark) {
    blocks.push(`html.dark { ${Object.entries(tokens.dark).map(([k, v]) => `${k}: ${v};`).join(' ')} }`);
  }
  style.textContent = blocks.join('\n');
}

function clearOverrides() {
  document.documentElement.style.removeProperty('--color-primary');
  document.documentElement.style.removeProperty('--color-accent');
  document.documentElement.style.removeProperty('--font-family-tenant');
  document.body.style.removeProperty('font-family');
  applyThemeStyle(null);
}

export function usePortalBranding() {
  const session = usePortalAuthStore((s) => s.session);
  const [branding, setBranding] = useState<PortalBranding | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session) {
      setBranding(null);
      setLoading(false);
      clearOverrides();
      return;
    }
    let cancelled = false;
    setLoading(true);
    getPortalBranding()
      .then((data) => {
        if (cancelled) return;
        setBranding(data);
        if (data.primaryColor) {
          document.documentElement.style.setProperty('--color-primary', data.primaryColor);
        }
        if (data.accentColor) {
          document.documentElement.style.setProperty('--color-accent', data.accentColor);
        }
        if (data.fontFamily) {
          document.documentElement.style.setProperty('--font-family-tenant', data.fontFamily);
          document.body.style.fontFamily = data.fontFamily;
        }
        applyThemeStyle(data.themeTokens);
        if (data.faviconUrl) {
          const link =
            document.querySelector<HTMLLinkElement>("link[rel='icon']") ||
            document.createElement('link');
          link.rel = 'icon';
          link.href = data.faviconUrl;
          document.head.appendChild(link);
        }
      })
      .catch(() => {
        if (!cancelled) setBranding(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [session]);

  return { branding, loading };
}
